import React, { useEffect, useState } from 'react';
import { DataTable } from '../components/DataTable';
import { LoadingBlock } from '../components/LoadingBlock';
import { SectionCard } from '../components/SectionCard';

const OFFICIATING_ROLES = [
  { code: 'head_referee', label: 'Árbitro principal' },
  { code: 'assistant_referee_1', label: 'Assistente 1' },
  { code: 'assistant_referee_2', label: 'Assistente 2' },
  { code: 'fourth_official', label: 'Quarto árbitro' },
  { code: 'var', label: 'VAR' },
  { code: 'avar', label: 'AVAR' },
];

function buildInitialState(match) {
  return {
    referee_user_id: '',
    referee_name: match?.referee_name || '',
    officiating_team: OFFICIATING_ROLES.map((role) => {
      const existing = (match?.officiating_team || []).find((item) => item.role_code === role.code);
      return {
        role_code: role.code,
        user_id: existing?.user_id ? String(existing.user_id) : '',
        display_name: existing?.display_name || '',
        notes: existing?.notes || '',
      };
    }),
  };
}

export function OfficiatingPage({ loading, error, matches, selectedMatch, referees, onSelectMatch, onSave, saving, canManage }) {
  const [form, setForm] = useState(buildInitialState(null));

  useEffect(() => {
    setForm(buildInitialState(selectedMatch || null));
  }, [selectedMatch]);

  if (loading) return <LoadingBlock label="Carregando arbitragem..." />;
  if (error) return <div className="page-error">{error}</div>;

  const matchesWithReferee = (matches || []).filter((match) => match.referee_name || (match.officiating_team || []).length);

  return (
    <div className="page-grid two-columns">
      <SectionCard title="Escala de arbitragem" subtitle="Defina árbitro principal, assistentes e observações técnicas.">
        <div className="callout-box compact-box admin-callout">
          <span className="eyebrow">Admin</span>
          <strong>Controle oficial da arbitragem</strong>
          <p>Selecione a partida, defina árbitro principal, equipe de apoio e ajuste observações por função. Isso mantém a escala profissional e rastreável.</p>
        </div>

        <DataTable
          rows={matches || []}
          columns={[
            { key: 'scheduled_at', label: 'Data' },
            { key: 'home_team_name', label: 'Mandante' },
            { key: 'away_team_name', label: 'Visitante' },
            { key: 'referee_name', label: 'Árbitro' },
            {
              key: 'actions',
              label: 'Ação',
              render: (row) => <button className="inline-button" type="button" onClick={() => onSelectMatch(row.id)}>Escalar</button>,
            },
          ]}
          emptyText="Sem jogos disponíveis para escala de arbitragem."
        />
      </SectionCard>

      <SectionCard title="Operação da arbitragem" subtitle="Edite a partida selecionada com equipe oficial.">
        {selectedMatch ? (
          <>
            <div className="callout-grid admin-summary-grid">
              <div className="callout-box compact-box"><span className="eyebrow">Partida</span><strong>{selectedMatch.home_team_name} x {selectedMatch.away_team_name}</strong></div>
              <div className="callout-box compact-box"><span className="eyebrow">Data</span><strong>{selectedMatch.scheduled_at || '-'}</strong></div>
              <div className="callout-box compact-box"><span className="eyebrow">Campo</span><strong>{selectedMatch.campo_name || '-'}</strong></div>
              <div className="callout-box compact-box"><span className="eyebrow">Escala atual</span><strong>{matchesWithReferee.length}</strong><p>Partidas com arbitragem já definida.</p></div>
            </div>

            {canManage ? (
              <form className="admin-form" onSubmit={(event) => {
                event.preventDefault();
                onSave(selectedMatch.id, {
                  referee_user_id: form.referee_user_id ? Number(form.referee_user_id) : null,
                  referee_name: form.referee_name || null,
                  officiating_team: form.officiating_team.filter((item) => item.user_id || item.display_name),
                });
              }}>
                <div className="form-row">
                  <label>
                    Árbitro principal por usuário
                    <select value={form.referee_user_id} onChange={(event) => setForm({ ...form, referee_user_id: event.target.value, referee_name: '' })}>
                      <option value="">Nome manual</option>
                      {(referees || []).map((referee) => <option key={referee.id} value={referee.id}>{referee.name}</option>)}
                    </select>
                  </label>
                  <label>
                    Nome manual do árbitro
                    <input value={form.referee_name} onChange={(event) => setForm({ ...form, referee_name: event.target.value, referee_user_id: '' })} />
                  </label>
                </div>

                <div className="detail-stack compact">
                  <strong>Equipe oficial</strong>
                  {form.officiating_team.map((official, index) => {
                    const role = OFFICIATING_ROLES.find((item) => item.code === official.role_code);
                    return (
                      <div key={official.role_code} className="form-row triple">
                        <label>
                          {role?.label || official.role_code}
                          <select
                            value={official.user_id}
                            onChange={(event) => setForm({
                              ...form,
                              officiating_team: form.officiating_team.map((item, itemIndex) => itemIndex === index ? { ...item, user_id: event.target.value } : item),
                            })}
                          >
                            <option value="">Nome manual</option>
                            {(referees || []).map((referee) => <option key={referee.id} value={referee.id}>{referee.name}</option>)}
                          </select>
                        </label>
                        <label>
                          Nome exibido
                          <input
                            value={official.display_name}
                            onChange={(event) => setForm({
                              ...form,
                              officiating_team: form.officiating_team.map((item, itemIndex) => itemIndex === index ? { ...item, display_name: event.target.value } : item),
                            })}
                          />
                        </label>
                        <label>
                          Observação
                          <input
                            value={official.notes}
                            onChange={(event) => setForm({
                              ...form,
                              officiating_team: form.officiating_team.map((item, itemIndex) => itemIndex === index ? { ...item, notes: event.target.value } : item),
                            })}
                          />
                        </label>
                      </div>
                    );
                  })}
                </div>

                <button className="primary-button" type="submit" disabled={saving}>{saving ? 'Salvando...' : 'Salvar arbitragem'}</button>
              </form>
            ) : <div className="empty-state">Seu perfil pode consultar, mas não editar a escala de arbitragem.</div>}
          </>
        ) : <div className="empty-state">Selecione uma partida para definir a arbitragem.</div>}
      </SectionCard>
    </div>
  );
}
